import React, {Component, ReactNode} from 'react'
import ResidentGeneralMeetingRepo, {ResidentGeneralMeetingInfo} from './ResidentGeneralMeetingRepo'
import {createResidentPage, ResidentPagePropsSet} from '../shared/hoc/createResidentPage'
import {StyledCard, StyledPageTitle} from '../reservations/components/CommonStyle'
import {StyledSecondaryButton} from '../../styledComponents/Button'
import {NatAsyncCallFunction} from '../../__NoAutomatedTests/common/util/Natutil'
import styled from 'styled-components'
import {currentMedia} from '../../styledComponents/ResponsiveConvenience'
import {
    ColumnFlexContainer,
    FullWidthLeftColumnFlexContainer,
    FullWidthLeftRowFlexContainer
} from '../../styledComponents/FlexComponents'
import {formatDateWithDayOfTheWeekFromDate} from '../../formatters/formatDate'
import {Box} from '@mui/material'
import {MiddleFontSpan} from '../assocDirector/AssocDirectorCommon'
import {Colors, FontSize, FontWeights} from '../../styledComponents/Constants'
import {CalendarMonth} from '@mui/icons-material'
import {PlainBackLink} from '../../styledComponents/Links'
import {DisplayItemDividerForPageTop} from '../../styledComponents/DisplayItem'

interface Props {
    condominiumCd: string
    residentGeneralMeetingRepo: ResidentGeneralMeetingRepo
}

interface State {
    residentGeneralMeetingInfoList: ResidentGeneralMeetingInfo[]
    isLoadCompleted: boolean
}

class ResidentPastGeneralMeetingListPage extends Component<Props & ResidentPagePropsSet, State> {
    constructor(props: Props & ResidentPagePropsSet) {
        super(props)

        this.state = {
            residentGeneralMeetingInfoList: [],
            isLoadCompleted: false,
        }

        this.renderGeneralMeetingCards = this.renderGeneralMeetingCards.bind(this)
        this.handleGeneralMeetingButtonClick = this.handleGeneralMeetingButtonClick.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {
            navigate,
            condominiumCd,
            residentGeneralMeetingRepo,
            eventEmitter,
        } = this.props

        await eventEmitter.showLoading()
        try {
            const response = await NatAsyncCallFunction(
                residentGeneralMeetingRepo.getResidentGeneralMeetingInfoList.bind(
                    residentGeneralMeetingRepo,
                    condominiumCd,
                    true,
                    false,
                ),
                navigate,
            )

            if (response === null) {
                return
            }

            this.setState({
                residentGeneralMeetingInfoList: response.details,
            })
        } finally {
            eventEmitter.closeLoading()
            this.setState({
                isLoadCompleted: true,
            })
        }
    }


    handleGeneralMeetingCardClick(generalMeetingId: number): void {
        this.props.navigate(
            `/resident/condominiums/${this.props.condominiumCd}/general-meetings/${generalMeetingId}`,
            {
                state: {
                    previousPageUrl: `/resident/condominiums/${this.props.condominiumCd}/general-meetings/past`,
                },
            },
        )
    }

    handleGeneralMeetingButtonClick(): void {
        this.props.navigate(`/resident/condominiums/${this.props.condominiumCd}/general-meetings`)
    }

    renderGeneralMeetingCards(): ReactNode {
        const {
            residentGeneralMeetingInfoList,
        } = this.state

        if (residentGeneralMeetingInfoList.length === 0) {
            return <Box marginTop={'15px'} marginBottom={'10px'}>
                <MiddleFontSpan>過去に開催された総会はありません</MiddleFontSpan>
            </Box>
        } else {
            return <ContentBody>
                <GeneralMeetingCardList>
                    {residentGeneralMeetingInfoList.map((generalMeetingInfo) => {
                        return <NoMarginStyledCard
                            key={generalMeetingInfo.generalMeetingId}
                            onClick={() => this.handleGeneralMeetingCardClick(generalMeetingInfo.generalMeetingId)}
                        >
                            <FullHeightColumnFlexContainer>
                                <CardMainArea>
                                    <NameArea>
                                        {generalMeetingInfo.generalMeetingName}
                                    </NameArea>
                                    <DateArea>
                                        <StyledCalendarMonth/>
                                        <Box width={'5px'}/>
                                        {formatDateWithDayOfTheWeekFromDate(generalMeetingInfo.generalMeetingDate)}
                                    </DateArea>
                                </CardMainArea>
                            </FullHeightColumnFlexContainer>
                        </NoMarginStyledCard>
                    })}
                </GeneralMeetingCardList>
                <Box height={'5px'}/>
            </ContentBody>
        }
    }

    render(): ReactNode {
        const {
            condominiumCd,
        } = this.props

        const {
            isLoadCompleted,
        } = this.state

        if (!isLoadCompleted) {
            return null
        }

        return <>
            <PlainBackLink
                to={`/resident/condominiums/${condominiumCd}/general-meetings`}
            >
                戻る
            </PlainBackLink>
            <StyledDisplayItemDividerForPageTop/>
            <Box height={'15px'}/>
            <StyledPageTitle>過去の総会一覧</StyledPageTitle>
            <Box height={'20px'}/>
            {this.renderGeneralMeetingCards()}
            <GeneralMeetingButton onClick={this.handleGeneralMeetingButtonClick}>
                現在の総会を見る
            </GeneralMeetingButton>
            <Box height={'15px'}/>
        </>
    }
}

export default createResidentPage(ResidentPastGeneralMeetingListPage)

const StyledDisplayItemDividerForPageTop = styled(DisplayItemDividerForPageTop)`
  margin-top: 14px;
  margin-bottom: 15px;
`

const ContentBody = styled.div`
  max-width: 738px;
  margin: auto;
`

const GeneralMeetingCardList = styled.div`
  display: flex;
  box-sizing: border-box;
  ${currentMedia.lessThan('sp')`
    flex-direction: column;
    align-items: center;
  `}
  ${currentMedia.greaterThan('pc')`
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
  `}

  width: 100%;
  margin: 15px 0;
  row-gap: 20px;
`

const NoMarginStyledCard = styled(StyledCard)`
  margin-top: 0;
`

const FullHeightColumnFlexContainer = styled(ColumnFlexContainer)`
  height: 100%;
`

const CardMainArea = styled(FullWidthLeftColumnFlexContainer)`
  height: 100%;
  padding: 10px 15px;
  gap: 5px;
  justify-content: space-between;
`

const NameArea = styled.div`
  font-size: ${FontSize.Body};
  color: ${Colors.AccentDark};
  font-weight: ${FontWeights.Bold};
  overflow-wrap: anywhere;
  line-break: anywhere;
`

const StyledCalendarMonth = styled(CalendarMonth)`
  color: ${Colors.AccentLight};
`

const DateArea = styled(FullWidthLeftRowFlexContainer)`
  height: 21px;
  font-size: ${FontSize.Small};
`

const GeneralMeetingButton = styled(StyledSecondaryButton)`
  margin-top: 30px;
`
